import React from "react";
import { Link } from "react-router-dom";
import { UserPlus, Lock, Server, Wrench, History, Trash2, LineChart, Sprout } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsToolkit — Sub-sprint D-α landing page.
 *
 * Catalogues the Ops endpoints exposed in D-α (read-only links).
 * Destructive actions (T3) are deferred to D-β and explicitly listed.
 */
const Tile = ({ to, title, body, icon: Icon, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-4 hover:border-emerald-300 hover:bg-emerald-50"
  >
    <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
      <Icon className="w-4 h-4 text-emerald-600" /> {title}
    </span>
    <p className="text-[11px] text-slate-500">{body}</p>
  </Link>
);

const RLAdminOpsToolkit = () => {
  const t = useRacketLabAdminT();
  return (
    <div data-testid="rl-admin-ops-toolkit-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-xl font-semibold text-slate-900">
            <Wrench className="w-5 h-5 text-emerald-600" /> {t("ops.toolkit.title")}
          </h1>
          <p className="text-xs text-slate-500">{t("ops.toolkit.page_subtitle")}</p>
        </div>
        <span data-testid="rl-ops-toolkit-scope" className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-medium text-amber-800">
          {t("ops.toolkit.scope_badge")}
        </span>
      </header>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <Tile
          to="/admin/racket-lab/ops/promote"
          title={t("ops.toolkit.tiles.promote.title")}
          body={t("ops.toolkit.tiles.promote.body")}
          icon={UserPlus}
          testId="rl-ops-tile-promote"
        />
        <Tile
          to="/admin/racket-lab/ops/gating"
          title={t("ops.toolkit.tiles.gating.title")}
          body={t("ops.toolkit.tiles.gating.body")}
          icon={Lock}
          testId="rl-ops-tile-gating"
        />
        <Tile
          to="/admin/racket-lab/ops/redis"
          title={t("ops.toolkit.tiles.redis.title")}
          body={t("ops.toolkit.tiles.redis.body")}
          icon={Server}
          testId="rl-ops-tile-redis"
        />
        <Tile
          to="/admin/racket-lab/ops/scan-audit"
          title={t("ops.toolkit.tiles.scan_audit.title")}
          body={t("ops.toolkit.tiles.scan_audit.body")}
          icon={LineChart}
          testId="rl-ops-tile-scan-audit"
        />
        <Tile
          to="/admin/racket-lab/ops/legacy"
          title={t("ops.toolkit.tiles.legacy.title")}
          body={t("ops.toolkit.tiles.legacy.body")}
          icon={History}
          testId="rl-ops-tile-legacy"
        />
        <Tile
          to="/admin/racket-lab/ops/retention"
          title={t("ops.toolkit.tiles.retention.title")}
          body={t("ops.toolkit.tiles.retention.body")}
          icon={Trash2}
          testId="rl-ops-tile-retention"
        />
        <Tile
          to="/admin/racket-lab/ops/seed-helper"
          title={t("ops.toolkit.tiles.seed_helper.title")}
          body={t("ops.toolkit.tiles.seed_helper.body")}
          icon={Sprout}
          testId="rl-ops-tile-seed-helper"
        />
      </section>

      <section data-testid="rl-ops-toolkit-deferred" className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[11px] text-emerald-700">
        <p className="font-medium">{t("ops.toolkit.complete_title")}</p>
        <p>{t("ops.toolkit.complete_body")}</p>
      </section>
    </div>
  );
};

export default RLAdminOpsToolkit;
